/**
 * The post-sign-in return target, carried as `?next=` on `/login` (ticket 825).
 * It lives in the URL so it survives the sign-in round trip, including the
 * social providers that never leave the page.
 *
 * `next` is attacker-controlled input, so it is validated as a same-origin
 * path before it is ever used as a navigation target (open-redirect guard).
 */

export const NEXT_PARAM = 'next';

const MAX_LENGTH = 2048;
const PLACEHOLDER_ORIGIN = 'https://return-to.invalid';

function hasControlCharacter(value: string): boolean {
    for (const char of value) {
        const code = char.codePointAt(0) ?? 0;
        if (code <= 0x1f || code === 0x7f) {
            return true;
        }
    }
    return false;
}

/** `/login` and anything under it, case-insensitively, on the decoded path. */
function isLoginPath(pathname: string): boolean {
    let decoded = pathname;
    try {
        decoded = decodeURIComponent(pathname);
    } catch {
        // Malformed escapes: fall back to the raw path.
    }
    return decoded.toLowerCase().startsWith('/login');
}

/** Returns a safe in-app path (`pathname + search + hash`), or `null`. */
export function readReturnTo(params: URLSearchParams): string | null {
    const raw = params.get(NEXT_PARAM);
    if (raw === null) {
        return null;
    }

    const value = raw.trim();
    if (
        value.length === 0 ||
        value.length > MAX_LENGTH ||
        !value.startsWith('/') ||
        value.startsWith('//') ||
        value.includes('\\') ||
        hasControlCharacter(value)
    ) {
        return null;
    }

    let url: URL;
    try {
        url = new URL(value, PLACEHOLDER_ORIGIN);
    } catch {
        return null;
    }

    if (url.origin !== PLACEHOLDER_ORIGIN) {
        return null;
    }
    // Re-check the normalised result, not just the input.
    if (url.pathname.startsWith('//') || isLoginPath(url.pathname)) {
        return null;
    }

    return `${url.pathname}${url.search}${url.hash}`;
}

/** `/login?next=%2Fadmin%2Fspots%3Fx%3D1` */
export function loginPathWithReturnTo(path: string): string {
    const params = new URLSearchParams({ [NEXT_PARAM]: path });
    return `/login?${params.toString()}`;
}
